import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithEmailLink, sendSignInLinkToEmail, isSignInWithEmailLink, signOut as firebaseSignOut, onAuthStateChanged, User, Auth } from 'firebase/auth';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Check if Firebase is configured
export const isFirebaseConfigured = Boolean(
  firebaseConfig.apiKey &&
  firebaseConfig.authDomain &&
  firebaseConfig.projectId
);

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

if (isFirebaseConfigured) {
  try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
  } catch (error) {
    // Use console.error here to avoid circular dependency with axiom
    console.error('Failed to initialize Firebase:', error);
  }
}

export { auth };

const googleProvider = new GoogleAuthProvider();

const notConfiguredError = new Error('Firebase is not configured. Please set VITE_FIREBASE_* environment variables.');

export async function signInWithGoogle() {
  if (!auth) {
    return { user: null, error: notConfiguredError };
  }
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return { user: result.user, error: null };
  } catch (error) {
    return { user: null, error: error as Error };
  }
}

export async function sendEmailSignInLink(email: string) {
  if (!auth) {
    return { success: false, error: notConfiguredError };
  }
  const actionCodeSettings = {
    url: `${window.location.origin}/auth/verify`,
    handleCodeInApp: true,
  };

  try {
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    // Save the email locally for verification
    window.localStorage.setItem('emailForSignIn', email);
    return { success: true, error: null };
  } catch (error) {
    return { success: false, error: error as Error };
  }
}

export async function completeEmailSignIn() {
  if (!auth) {
    return { user: null, error: notConfiguredError };
  }
  if (!isSignInWithEmailLink(auth, window.location.href)) {
    return { user: null, error: new Error('Invalid sign-in link') };
  }

  let email = window.localStorage.getItem('emailForSignIn');
  if (!email) {
    // User opened link on different device, ask for email
    email = window.prompt('Please provide your email for confirmation');
  }

  if (!email) {
    return { user: null, error: new Error('Email required') };
  }

  try {
    const result = await signInWithEmailLink(auth, email, window.location.href);
    window.localStorage.removeItem('emailForSignIn');
    return { user: result.user, error: null };
  } catch (error) {
    return { user: null, error: error as Error };
  }
}

export async function signOut() {
  if (!auth) {
    return { success: false, error: notConfiguredError };
  }
  try {
    await firebaseSignOut(auth);
    return { success: true, error: null };
  } catch (error) {
    return { success: false, error: error as Error };
  }
}

export function subscribeToAuthChanges(callback: (user: User | null) => void) {
  if (!auth) {
    // Return a no-op unsubscribe function
    callback(null);
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
}

export type { User };
